import { Injectable, OnDestroy } from '@angular/core';
import { ApolloQueryResult } from 'apollo-client';
import { gql } from 'graphql-tag';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  Clinic,
  ClinicSetup,
  Doctor,
  FormTemplate,
  QuestionnaireType,
  TemperatureUnit
} from 'src/API';
import { StaffService } from 'src/app/core/api/staff.service';
import { AppStateService } from 'src/app/core/services/app-state.service';
import { TextFavorite, UpdateClinicSetupInput, UpdateClinicSetupMutation } from './../../../API';
import { updateClinicSetup } from './../../../graphql/mutations';
import { AppSyncService } from './../../core/appsync.service';
import { DryEyeSeverity } from './../../core/remote-json/treatments/dry-eye-category.model';
import {
  FormTemplateSchema,
  FormTemplateSchemaType
} from './../../form-template/form-template-model';
import { InteractionsSetup } from './../interactions-setup-modal/interactions-setup.model';
import { ClinicSetupFormGroup } from './clinic-setup-form.model';

export type QuestionnaireScoreConfig = {
  [keys in QuestionnaireType]?: {
    scoreConfig: {
      score: number;
      comparator: '=' | '<' | '>';
      message: DryEyeSeverity;
    }[];
  };
};

export type ReminderSchedule = {
  disabled: boolean;
  name: string;
  friendlyName: string;
};

@Injectable({
  providedIn: 'root'
})
export class ClinicSetupService implements OnDestroy {
  public textFavorites: TextFavorite[];
  public interactionsSetup: InteractionsSetup;
  public isClinicAdmin: boolean;
  public clinicOwnerId: string;
  public isSaving: boolean;
  public temperatureInCelsius: string | number;
  public clinicSetupTemperatureUnit: TemperatureUnit;
  public hiddenTreatmentTemplates: string[];
  public clinicStaffs: Doctor[];
  public clinicSetup: ClinicSetup;

  public clinicSetupFormGroup = new ClinicSetupFormGroup();

  public clinicSetupId: string;
  public clinicId: string;
  public clinic: Clinic;

  public formTemplates$: Observable<any[]> = this.appStateService.clinic$.pipe(
    untilDestroyed(this),
    map(clinic => {
      return clinic && clinic['clinicFormTemplates']
        ? clinic['clinicFormTemplates'].items.map(formTemplate => {
            return {
              ...formTemplate,
              schema: JSON.parse(formTemplate.schema) as FormTemplateSchema
            };
          })
        : [];
    })
  );
  public formTemplateKeyToTemplatesMap: {
    [key: string]: (Omit<FormTemplate, 'schema'> & { schema: FormTemplateSchema })[];
  } = Object.keys(FormTemplateSchemaType).reduce((acc, key) => {
    acc[key] = [];
    return acc;
  }, {});

  public formTemplateTypeToTemplatesMap: {
    [key: string]: (Omit<FormTemplate, 'schema'> & { schema: FormTemplateSchema })[];
  };

  public intakeFormTemplates;
  public questionnaireScoreConfig: QuestionnaireScoreConfig;
  constructor(
    private staffService: StaffService,
    private appSyncService: AppSyncService,
    private appStateService: AppStateService
  ) {
    this.formTemplates$.pipe(untilDestroyed(this)).subscribe(formTemplates => {
      this.formTemplateTypeToTemplatesMap = formTemplates.reduce((acc, template) => {
        acc[template.schema.type] = acc[template.schema.type]
          ? [...acc[template.schema.type], template]
          : [template];
        return acc;
      }, {});
    });
  }

  public setClinicSetup(doctor: Doctor) {
    if (doctor && doctor.clinic) {
      this.clinic = doctor.clinic;
      this.clinicId = doctor.clinic.id;
      this.clinicOwnerId = doctor.clinic.owner.id;
      this.isClinicAdmin = this.staffService.isClinicOwner || this.staffService.isClinicAdmin;
      this.clinicStaffs = doctor.clinic.doctors.items;

      if (doctor.clinic.clinicSetup) {
        this.clinicSetupFormGroup.patchValueFromClinicSetup(
          doctor.clinic.clinicSetup,
          doctor.clinic.owner.email
        );

        this.clinicSetup = doctor.clinic.clinicSetup;

        this.clinicSetupId = doctor.clinic.clinicSetup.id;
        this.interactionsSetup = JSON.parse(doctor.clinic.clinicSetup.interactionsSetup || '{}');
        this.clinicSetupTemperatureUnit = doctor.clinic.clinicSetup.temperatureUnit;
        this.hiddenTreatmentTemplates = this.clinicSetupFormGroup.controls.hiddenTreatmentTemplates.value;
        this.questionnaireScoreConfig = this.clinicSetupFormGroup.controls.questionnaireScoreConfig.value;
        this.clinicSetup = doctor.clinic.clinicSetup;
      }
    }
  }

  ngOnDestroy(): void {}
  public getReminderSchedule(): ReminderSchedule[] {
    return [
      {
        disabled: false,
        friendlyName: ' 1 Days Before',
        name: '1'
      },
      {
        disabled: false,
        friendlyName: '2 Days Before',
        name: '2'
      },
      {
        disabled: false,
        friendlyName: '3 Days Before',
        name: '3'
      },
      {
        disabled: false,
        friendlyName: '4 Days Before',
        name: '4'
      },
      {
        disabled: false,
        friendlyName: '5 Days Before',
        name: '5'
      },
      {
        disabled: false,
        friendlyName: '6 Days Before',
        name: '6'
      }
    ];
  }

  public updateClinicSetup(input: UpdateClinicSetupInput): Observable<ClinicSetup> {
    return this.appSyncService.hydrated().pipe(
      switchMap(client => {
        return client.mutate({
          mutation: gql(updateClinicSetup),
          variables: { input }
        });
      }),
      map((clinicSetupMutation: ApolloQueryResult<UpdateClinicSetupMutation>) => {
        this.clinicSetup = clinicSetupMutation.data.updateClinicSetup;

        return this.clinicSetup;
      })
    );
  }
}
